import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  RowContainer,
  ElementContainer,
  ColumnContainer,
  Card,
} from "../../components/containers/styledComponents";
import { CompanyLogo } from "../../components/tables/CompanyLogo";
import {
  PathwayProgressBar,
  getPathwayFromInput,
} from "../../components/visuals/PathwayProgressBar";
import { CompassParagraph } from "../../components/common/styledComponents";
import { formatPct } from "../../utils/formattingUtils";
import { greyText, lightGreys, statusBadgeColors } from "../../styles";
import {
  GetControlPanelDataParams,
  TopContributorItem,
} from "../../types/controlPanelTypes";
import { usePost } from "../../hooks/usePost";
import { CircleTag } from "../../components/visuals/styledComponents";
import { BadgeStatus } from "../../enums/Status";

const OverflowContainer = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  height: 99999px;
  max-height: 90%;
  width: 100%;
`;

const StyledRowContainer = styled(RowContainer)`
  border-bottom: 1px solid ${lightGreys.borderLightGrey};
  padding: 10px;
  padding-top: 5px;
  padding-bottom: 5px;
`;

const ContributorsCard = styled(Card)`
  flex: 1 1 0;
  min-width: 440px;
`;

const FlexWrapper = styled.div<{ $flex: number }>`
  display: flex;
  flex-direction: row;
  flex: ${({ $flex }): number => $flex} 1 0;
  min-width: 0;
`;

const COMPANY_FLEX = 2.3;
const CONTRIBUTION_FLEX = 1.4;
const RANK_FLEX = 0.7;
const PATHWAY_FLEX = 1.2;

const HeaderRow = styled(RowContainer)`
  padding: 0 10px;
`;

const addRankToContributors = (
  contributors: TopContributorItem[],
): TopContributorItem[] => {
  const sortedContributors = [...contributors].sort((a, b) => b.rank - a.rank);

  return sortedContributors.map((contributor, index) => ({
    ...contributor,
    rank: index + 1,
  }));
};

export const TopContributors = ({
  params,
  height = "500px",
}: TopContributorsProps): JSX.Element => {
  const { data, sendData, isLoading } = usePost<
    TopContributorItem[],
    GetControlPanelDataParams
  >();
  const [companyData, setCompanyData] = useState<TopContributorItem[]>([]);

  useEffect(() => {
    sendData("GET_TOP_CONTRIBUTORS", params);
  }, [params]);

  useEffect(() => {
    if (data) {
      setCompanyData(addRankToContributors(data));
    }
  }, [data]);

  useEffect(() => {
    setCompanyData(addRankToContributors([]));
  }, []);

  return (
    <ContributorsCard h={height} m="0 20px 0 10px" isLoading={isLoading}>
      <CompassParagraph bold={true} fontSize={16}>
        Top Contributors
      </CompassParagraph>

      <HeaderRow>
        {HEADERS.map(({ label, flex }) => (
          <FlexWrapper key={label} $flex={flex}>
            <ElementContainer>
              <CompassParagraph bold={true} color={greyText} fontSize={14}>
                {label}
              </CompassParagraph>
            </ElementContainer>
          </FlexWrapper>
        ))}
      </HeaderRow>
      <OverflowContainer>
        <ColumnContainer jc="flex-start" ai="flex-start" mt={1}>
          {companyData.map((item, index) => (
            <StyledRowContainer key={`${index}-${item.name}`} ai="center">
              <FlexWrapper $flex={COMPANY_FLEX}>
                <CompanyLogo
                  name={item.name}
                  companyId={item.compass_cid}
                  logoUrl={item.image}
                />
              </FlexWrapper>
              <FlexWrapper $flex={CONTRIBUTION_FLEX}>
                <CompassParagraph bold={true}>
                  {formatPct(item.contribution)}
                </CompassParagraph>
              </FlexWrapper>

              <FlexWrapper $flex={RANK_FLEX}>
                <CircleTag
                  color={statusBadgeColors[BadgeStatus.PLANNED].bg}
                  size={24}
                  textColor={statusBadgeColors[BadgeStatus.PLANNED].text}
                >
                  {item.rank}
                </CircleTag>
              </FlexWrapper>
              <FlexWrapper $flex={PATHWAY_FLEX}>
                <PathwayProgressBar
                  pathwayItem={getPathwayFromInput(
                    item.implicit_temperature_value,
                  )}
                />
              </FlexWrapper>
            </StyledRowContainer>
          ))}
        </ColumnContainer>
      </OverflowContainer>
    </ContributorsCard>
  );
};

const HEADERS: { label: string; flex: number }[] = [
  { label: "Company", flex: COMPANY_FLEX },
  { label: "Contribution", flex: CONTRIBUTION_FLEX },
  { label: "Rank", flex: RANK_FLEX },
  { label: "Pathway", flex: PATHWAY_FLEX },
];

type TopContributorsProps = {
  params: GetControlPanelDataParams;
  height?: string;
};
